import * as React from 'react'
import classNames from 'classnames'
import { useTranslation } from 'translations'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import { getGoogleTTDCategories } from '../../consts'
import { blockedCategories, categoryConflicts } from '../../model/categories'
import './categoryChips.scss'

interface CategoryChipsProps {
    selected: string[]
    onChange: (selected: string[]) => void
    disabled?: boolean
    /** Marks the field as missing when the page asks for errors. */
    markError?: boolean
    ariaLabelledBy: string
}

/**
 * Google categories as toggle chips. The other half of an exclusive pair greys
 * out as soon as one half is on; a pair that is already in conflict (old data)
 * shows both halves in error until one is removed.
 */
export function CategoryChips({ selected, onChange, disabled, markError, ariaLabelledBy }: CategoryChipsProps) {
    const { t } = useTranslation()
    const options = React.useMemo(() => getGoogleTTDCategories(), [t])
    const blocked = blockedCategories(selected)
    const conflicting = new Set(categoryConflicts(selected).flat())
    const labelOf = (value: string) => options.find((option) => option.value === value)?.name ?? value

    const toggle = (value: string) => {
        if (selected.includes(value)) {
            onChange(selected.filter((item) => item !== value))
        } else {
            onChange([...selected, value])
        }
    }

    return (
        <div className="category-chips">
            <div
                className={classNames('category-chips-list', { 'has-error': markError && selected.length === 0 })}
                role="group"
                aria-labelledby={ariaLabelledBy}
            >
                {options.map((option) => {
                    const on = selected.includes(option.value)
                    const blocker = blocked[option.value]
                    const chip = (
                        <button
                            key={option.value}
                            type="button"
                            className={classNames('category-chip', {
                                on,
                                conflict: conflicting.has(option.value),
                                blocked: !!blocker,
                            })}
                            aria-pressed={on}
                            disabled={disabled || !!blocker}
                            onClick={() => toggle(option.value)}
                        >
                            {option.name}
                        </button>
                    )
                    return blocker ? (
                        <DisabledItemHoverInfotip
                            key={option.value}
                            active
                            width="16em"
                            infotipText={t('channels.googleTTD.categories.blockedBy', { category: labelOf(blocker) })}
                        >
                            {chip}
                        </DisabledItemHoverInfotip>
                    ) : (
                        chip
                    )
                })}
            </div>
            {conflicting.size > 0 && (
                <p className="category-chips-error" role="alert">
                    {t('channels.googleTTD.categories.conflict', {
                        first: labelOf(categoryConflicts(selected)[0][0]),
                        second: labelOf(categoryConflicts(selected)[0][1]),
                    })}
                </p>
            )}
        </div>
    )
}
